import type { AreaId, Me } from '../api/types'
import { adminNav } from './admin/nav'
import { appNav } from './app/nav'
import type { AreaDefinition } from './types'
import { workspaceNav } from './workspace/nav'

export const AREAS: Record<AreaId, AreaDefinition> = {
  app: {
    id: 'app',
    label: 'Investor',
    kicker: 'Investor workspace',
    basePath: '/app',
    scope: 'Investor view: published research only.',
    nav: appNav,
  },
  workspace: {
    id: 'workspace',
    label: 'Workspace',
    kicker: 'Fruuit workspace',
    basePath: '/workspace',
    scope: 'Fruuit team view: internal research and review controls.',
    nav: workspaceNav,
  },
  admin: {
    id: 'admin',
    label: 'Admin',
    kicker: 'Platform admin',
    basePath: '/admin',
    scope: 'Platform administration: organisations, access, subscriptions and data feeds.',
    nav: adminNav,
  },
}

/** Switcher order. */
export const AREA_ORDER: AreaId[] = ['app', 'workspace', 'admin']

export function permittedAreas(me: Me): AreaId[] {
  return AREA_ORDER.filter((area) => me.areas.includes(area))
}

/** Where a signed-in user lands: the first permitted area (ARCHITECTURE §6). */
export function homePath(me: Me): string | null {
  const first = permittedAreas(me)[0]
  return first ? AREAS[first].basePath : null
}

/** Org context shown in the top bar for an area. */
export function orgLabel(me: Me, area: AreaId): string {
  if (area === 'admin') return 'Platform administration'
  const kind = area === 'app' ? 'investor' : 'fruuit'
  const roles = area === 'app' ? ['investor', 'investor_admin'] : ['manager', 'reviewer']
  const membership = me.memberships.find(
    (m) => m.org_kind === kind || (m.org_kind === undefined && roles.includes(m.role)),
  )
  return membership?.org_name ?? (area === 'app' ? 'Investor organisation' : 'Fruuit Consulting')
}

const ROLE_LABELS: Record<string, string> = {
  investor: 'Investor',
  investor_admin: 'Investor admin',
  manager: 'Engagement manager',
  reviewer: 'Reviewer',
  platform_admin: 'Platform admin',
  platform_operator: 'Platform operator',
}

export function roleSummary(me: Me): string {
  const labels = [
    ...(me.platform_role ? [me.platform_role] : []),
    ...me.memberships.map((m) => m.role),
  ].map((role) => ROLE_LABELS[role] ?? role)
  return Array.from(new Set(labels)).join(' · ')
}
